import { StyleProp } from '@tldraw/tldraw'
import { Color } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { useRelevantStyles } from 'components/VisualStudioPlayground/VisualEditor/hooks/useRelevantStyles'
import { useEditor } from 'components/VisualStudioPlayground/VisualEditorContext'
import { useEffect } from 'react'
import { useRootStore } from 'stores/RootStore'
import {
  IFillStyle,
  IStrokeColorStyle,
  ITextBoxBackgroundFillStyle,
} from 'components/VisualStudioPlayground/VisualEditor/StyleProps'

/**
 * This component observes the style prop and opens the colors panel when the style prop is present on all selected shapes.
 * It also closes the colors panel when the style prop is not present on all selected shapes.
 */
export const StylePropObserver = ({
  styleProp,
}: {
  styleProp: StyleProp<IFillStyle | IStrokeColorStyle | ITextBoxBackgroundFillStyle>
}) => {
  const { designStore } = useRootStore()
  const panel = designStore?.panel
  const editor = useEditor()
  const relevantStyles = useRelevantStyles(editor)

  const selectedShapes = editor.getSelectedShapes()
  const allShapesHaveStyleProp = selectedShapes.every((shape) => {
    const hasStyleProp = Boolean(editor.getShapeStyleIfExists(shape, styleProp))
    return hasStyleProp
  })
  const shouldBeOpen = allShapesHaveStyleProp

  useEffect(
    function closeColorsPanelIfNeeded() {
      if (!shouldBeOpen) {
        designStore?.closePanel()
      }
    },
    [shouldBeOpen, designStore],
  )

  const selectedShapesIds = selectedShapes.map((shape) => shape.id)
  useEffect(
    function reinitialiseColorsPanelOnSelectionChange() {
      const style = relevantStyles?.styles.get(styleProp)
      if (!style || !panel || panel.type !== 'colors') return
      let color = style.type === 'shared' ? style.value : undefined
      // This is necessary because the strike colors are stored as strings for now
      if (typeof color === 'string') {
        color = { type: 'solidColor', color, opacity: 1 }
      }

      designStore.openPanel({
        ...panel,
        defaultColor: color as Color,
      })
    },
    [JSON.stringify(selectedShapesIds), panel?.type], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return null
}
